import {
  createInstance,
  OptimizelyContext,
  useFeature as useFeatureOriginal,
} from "@optimizely/react-sdk";
import cookie from "js-cookie";
import { useContext } from "react";

export const optimizely = createInstance({
  sdkKey: `${
    process.env.BUILD_ENV === "prod"
      ? process.env.PROD_OPTIMIZELY_SDK_KEY
      : process.env.NONPROD_OPTIMIZELY_SDK_KEY
  }`,
});

type DefaultProps = {
  enabled: boolean;
  variables?: {
    [key: string]: any;
  };
};

/*
  Using hook for optimizely context allows us to mock optimizely in jest.

  jest.mock('@/src/utils/useOptimizely', () => ({
    useOptimizely: jest.fn().mockImplementation(() => ({
      setForcedVariation: jest.fn(),
      track: jest.fn(),
    })),
  }));
*/
function useOptimizely() {
  const { optimizely } = useContext(OptimizelyContext);

  return optimizely;
}

function useFeature(feature: string, defaultProps?: DefaultProps) {
  const result = useFeatureOriginal(feature);
  const optimizely = useOptimizely();
  const [, ...tail] = result;

  if (featureMatches(feature)) {
    const { enabled, variables } = getFeatureObject(feature);

    const isDisabled = getFeatureDisabled();

    const finalEnabledValue = !isDisabled && enabled;
    return [finalEnabledValue, ...(variables ? [variables] : tail)] as const;
  }

  if (defaultProps) {
    const isFeatureExists = Object.keys(
      optimizely?.getOptimizelyConfig()?.featuresMap ?? {}
    ).includes(feature);

    if (!isFeatureExists) {
      const { enabled, variables } = defaultProps;
      return [enabled, ...(variables ? [variables] : tail)] as const;
    }
  }

  return result;
}

function featureMatches(feature: string) {
  const value = getValue("optimizelyFeature");
  const { enabled, variables } = getFeatureObject(feature);
  const hasFeatureKey = Boolean(enabled && variables);

  return (
    value === "*" ||
    value?.split(",").includes(feature) ||
    hasFeatureKey ||
    getFeatureDisabled() ||
    value?.includes(feature)
  );
}

function getFeatureDisabled(): boolean {
  return getValue("optimizelyFeatureDisabled") === "true";
}

function getValue(key: string) {
  const urlParams = new URLSearchParams(window.location.search);
  // Cookie used for e2e tests mainly
  return urlParams.get(key) || cookie.get(key);
}

function getFeatureObject(feature: string) {
  const value = getValue("optimizelyFeature");
  const defaultObj = { enabled: false, variables: {} };
  try {
    const featureObj = value ? JSON.parse(value) : defaultObj;
    return featureObj[feature] || defaultObj;
  } catch {
    return defaultObj;
  }
}

// Export everything first and then override with our own implementations
export * from "@optimizely/react-sdk";
// Ignore exports when no active tests are in progress
// ts-unused-exports:disable-next-line
export { useFeature };
